import { computed, defineComponent, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { useDateFormat } from '@/composables/dateFormat';
import { ProjectAction, ProjectGetter } from '@/store/project/project.module';
import { ProjectAttachment, ApprovementForm, BudgetForm, Project, ProjectForm } from '@/store/project/project.model';
import { ProjectResultAction } from '@/store/projectResult/projectResult.module';
import BaseOffcanvasComponent from '@/components/baseOffcanvas/BaseOffcanvas.vue';
import ProjectModalComponent from '../projectModal/ProjectModal.vue';
import BudgetModalComponent from '../budgetModal/BudgetModal.vue';
import ApprovementModalComponent from '../approvementModal/ApprovementModal.vue';

export default defineComponent({
  name: 'ProjectOffcanvasComponent',
  components: {
    BaseOffcanvasComponent,
    ProjectModalComponent,
    BudgetModalComponent,
    ApprovementModalComponent,
  },
  props: {
    projectId: {
      type: Number,
    }
  },
  emits: [
    'attachment',
    'image',
  ],
  setup: (props, context) => {
    const store = useStore();
    const swal = useSweetAlert();
    const dateFormat = useDateFormat();
    const projectOffcanvas = ref<InstanceType<typeof BaseOffcanvasComponent>>();
    const projectModal = ref<InstanceType<typeof ProjectModalComponent>>();
    const budgetModal = ref<InstanceType<typeof BudgetModalComponent>>();
    const approvementModal = ref<InstanceType<typeof ApprovementModalComponent>>();
    const project = computed<Project | undefined>(() => store.getters[ProjectGetter.BY_ID](props.projectId));
    const attachments = computed<ProjectAttachment[]>(() => (project.value) ? project.value?.attachments.filter(m => m.attachmentType.id === 1) : []);
    const images = computed<ProjectAttachment[]>(() => (project.value) ? project.value?.attachments.filter(m => m.attachmentType.id === 2) : []);
    const photoPreview = computed<string>(() => project.value?.projectApprovement
      ? project.value.projectApprovement.ministerPhoto
      : require('@/assets/images/user.png'));

    const show = () => {
      projectOffcanvas.value?.show();
    };

    const hide = () => {
      projectOffcanvas.value?.hide();
    };

    const editClick = (mode: string) => {
      switch (mode) {
        default:
          projectModal.value?.show();
          break;
        case 'budget':
          budgetModal.value?.show();
          break;
        case 'approvement':
          approvementModal.value?.show();
          break;
      }
    };

    // update project
    const updateProject = (projectData: ProjectForm) => {
      store.dispatch(ProjectAction.UPDATE, {
        id: project.value?.id,
        name: projectData.name,
        description: projectData.description,
        provinceId: project.value?.province.id,
        projectTypeId: projectData.projectTypeId,
        fiscalYearId: project.value?.fiscalYear.id,
        projectApprovement: {
          cabinetMinisterId: project.value?.projectApprovement?.id,
          approvedDate: project.value?.projectApprovement?.approvedDate
        },
        projectBudgets: project.value?.projectBudgets.map(m => {
          return {
            budgetTypeId: m.id,
            amount: m.amount
          };
        })
      });
    };

    // update budgets
    const updateBudgets = (budgetData: BudgetForm[]) => {
      const currentBudgets = (project.value) ? project.value?.projectBudgets.filter(m => m.id !== 4).reduce((total, m) => total + (m.amount ? m.amount : 0), 0) : 0;
      store.dispatch(ProjectResultAction.UPDATE_REGION_RESULT, {
        id: project.value?.province.id,
        amount: -1 * currentBudgets + budgetData.filter(m => m.budgetTypeId === 1 || m.budgetTypeId === 2 || m.budgetTypeId === 5).reduce((total, m) => total + (m.amount ? m.amount : 0), 0)
      })
        .then(() => store.dispatch(ProjectAction.UPDATE, {
          id: project.value?.id,
          name: project.value?.name,
          description: project.value?.description,
          provinceId: project.value?.province.id,
          projectTypeId: project.value?.projecType.id,
          fiscalYearId: project.value?.fiscalYear.id,
          projectApprovement: {
            cabinetMinisterId: project.value?.projectApprovement?.id,
            approvedDate: project.value?.projectApprovement?.approvedDate
          },
          projectBudgets: budgetData.map(m => {
            return {
              budgetTypeId: m.budgetTypeId,
              amount: m.amount
            };
          })
        }));
    };

    // update approvement
    const updateApprovement = (approvementData: ApprovementForm) => {
      store.dispatch(ProjectAction.UPDATE, {
        id: project.value?.id,
        name: project.value?.name,
        description: project.value?.description,
        provinceId: project.value?.province.id,
        projectTypeId: project.value?.projecType.id,
        fiscalYearId: project.value?.fiscalYear.id,
        projectApprovement: {
          cabinetMinisterId: approvementData.cabinetMinisterId,
          approvedDate: approvementData.approvedDate
        },
        projectBudgets: project.value?.projectBudgets.map(m => {
          return {
            budgetTypeId: m.id,
            amount: m.amount
          };
        })
      });
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            const currentBudgets = (project.value) ? project.value?.projectBudgets.filter(m => m.id === 1).reduce((total, m) => total + (m.amount ? m.amount : 0), 0) : 0;
            const currentResponses = (project.value) ? project.value?.projectBudgets.filter(m => m.id === 2).reduce((total, m) => total + (m.amount ? m.amount : 0), 0) : 0;
            store.dispatch(ProjectResultAction.UPDATE_REGION_RESULT, {
              id: project.value?.province.id,
              budget: -1 * currentBudgets,
              residual: -1 * currentResponses,
            })
              .then(() => store.dispatch(ProjectAction.DELETE, {
                fiscalYearId: project.value?.fiscalYear.id,
                id: project.value?.id
              }))
                .then(() => hide());
          }
        });
    };

    const attachmentClick = () => {
      context.emit('attachment', props.projectId);
    };

    const imageClick = () => {
      context.emit('image', props.projectId);
    };

    const thaiDate = (date: Date) =>
      dateFormat.toThaiDate(date, 'long');

    return {
      project,
      attachments,
      images,
      photoPreview,
      projectOffcanvas,
      projectModal,
      budgetModal,
      approvementModal,
      show,
      hide,
      editClick,
      updateProject,
      updateBudgets,
      updateApprovement,
      deleteClick,
      attachmentClick,
      imageClick,
      thaiDate,
    };
  },
});
